// Test tozalash — test serverdagi sinov ma'lumotlarini o'chirish (Khorezmai, 2026-10-02).
// Faqat test hostda ko'rinadi (isTestEnv) — prod (app.tezhal.uz) da menyuda ham, sahifada ham yo'q.
// Backend ham alohida tekshiradi: admin + test host + «TOZALASH» tasdig'i (routes/admin/testCleanup.js).
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../api';
import { PageHeader, Loading, ErrorBanner, ui } from '../components/adminUi';
import Icon from '../components/Icon';
import { isTestEnv } from '../lib/testEnv';

// Backend kategoriya yorlig'i oldida emoji keladi — ko'rsatishda olib tashlanadi,
// o'rniga kategoriya kaliti bo'yicha chiziqli ikonka chiqadi.
const LEADING_EMOJI = /^[\p{Extended_Pictographic}\uFE0F\u200D\s]+/u;
const stripEmoji = (label) => String(label || '').replace(LEADING_EMOJI, '');
const CATEGORY_ICONS = {
  orders: 'package',
  disputes: 'scale',
  reviews: 'star',
  calls: 'phoneCall',
  notifications: 'bell',
  reports: 'flag',
  credits: 'creditCard',
  otp: 'phone',
};

export default function TestCleanupPage() {
  const { auth } = useAuth();
  const [phone, setPhone] = useState('');
  const [appliedPhone, setAppliedPhone] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState('');

  const load = useCallback(async (ph) => {
    setLoading(true); setError('');
    try {
      const q = ph ? `?phone=${encodeURIComponent(ph)}` : '';
      const res = await apiFetch(`/api/admin/test-cleanup${q}`, auth.token);
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d.message || 'Yuklab bo\'lmadi'); setData(null); return; }
      setData(d); setAppliedPhone(ph || '');
    } catch {
      setError('Tarmoq xatosi. Qayta urinib ko\'ring.');
    } finally { setLoading(false); }
  }, [auth.token]);

  useEffect(() => { if (isTestEnv()) load(''); }, [load]);

  const scopeText = appliedPhone ? ` (faqat ${appliedPhone} raqami)` : ' (HAMMA foydalanuvchilar)';

  const clean = async (category, rawLabel) => {
    const label = stripEmoji(rawLabel);
    if (category === 'all') {
      const typed = window.prompt(`Test bazadagi BARCHA sinov ma'lumoti${scopeText} o'chiriladi. Qaytarib bo'lmaydi.\n\nDavom etish uchun TOZALASH deb yozing:`);
      if (typed !== 'TOZALASH') return;
    } else if (!window.confirm(`«${label}»${scopeText} o'chirilsinmi? Qaytarib bo'lmaydi.`)) {
      return;
    }
    setBusy(category); setError(''); setResult('');
    try {
      const res = await apiFetch('/api/admin/test-cleanup', auth.token, {
        method: 'POST',
        body: JSON.stringify({ category, confirm: 'TOZALASH', phone: appliedPhone || undefined }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d.message || 'Tozalashda xatolik'); return; }
      const parts = Object.entries(d.deleted || {}).filter(([, n]) => n > 0).map(([t, n]) => `${t}: ${n}`);
      setResult(`${label} tozalandi — ${d.total || 0} ta yozuv${parts.length ? ` (${parts.join(', ')})` : ''}`);
      await load(appliedPhone);
    } catch {
      setError('Tarmoq xatosi. Qayta urinib ko\'ring.');
    } finally { setBusy(''); }
  };

  if (!isTestEnv()) {
    return (
      <AdminLayout>
        <PageHeader title="Test tozalash" />
        <ErrorBanner>Bu bo'lim faqat test serverida ishlaydi.</ErrorBanner>
      </AdminLayout>
    );
  }

  const otp = data && data.categories.find((c) => c.key === 'otp');

  return (
    <AdminLayout>
      <PageHeader title={<><Icon name="trash" size={24} style={{ marginRight: '8px' }} />Test tozalash</>} subtitle="Test serverdagi sinov ma'lumotlarini o'chirish — foydalanuvchilar, ustalar va sozlamalar saqlanadi">
        <button className="th-btn-primary" style={ui.addBtn} onClick={() => load(appliedPhone)} disabled={loading}><Icon name="refresh" size={18} style={{ marginRight: '8px' }} />Yangilash</button>
      </PageHeader>

      <div style={s.warn} role="alert">
        <Icon name="alert" size={22} />
        <span>O'chirilgan ma'lumotni qaytarib bo'lmaydi. Faqat TEST serverida ishlaydi — prod (tezhal.uz) bazasiga tegmaydi.</span>
      </div>

      <div style={s.card}>
        <label style={s.fieldLabel}><Icon name="phone" size={18} style={{ marginRight: '8px' }} />Telefon bo'yicha (ixtiyoriy)</label>
        <div style={s.row}>
          <input
            className="th-field"
            style={{ ...ui.input, flex: 1, minWidth: '220px', transition: 'border-color 0.2s, box-shadow 0.2s' }}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load(phone.trim()); }}
            placeholder="901234567 yoki +998901234567 — bir nechtasini vergul bilan"
          />
          <button style={{ ...s.rowBtn, ...s.secondaryBtn }} onClick={() => load(phone.trim())} disabled={loading}><Icon name="search" size={18} style={{ marginRight: '8px' }} />Tekshirish</button>
          {appliedPhone && (
            <button style={{ ...s.rowBtn, ...s.secondaryBtn }} onClick={() => { setPhone(''); load(''); }}><Icon name="x" size={18} style={{ marginRight: '8px' }} />Hammasi</button>
          )}
          <button
            className="th-danger-btn"
            style={{ ...s.rowBtn, ...s.dangerBtn }}
            disabled={!!busy || !otp}
            title="Kunlik SMS chegarasi va eski kodlar — darhol qayta kod so'rash mumkin bo'ladi"
            onClick={() => clean('otp', 'SMS kodlar va limit')}
          ><Icon name="phone" size={18} style={{ marginRight: '8px' }} />SMS limitni tozalash</button>
        </div>
        <div style={s.scope}>
          {data && data.scope
            ? (data.scope.users.length
              ? <><Icon name="target" size={18} style={{ marginRight: '8px' }} />Faqat: {data.scope.users.map((u) => `${u.name} (${u.role})`).join(', ')}</>
              : <><Icon name="alert" size={18} style={{ marginRight: '8px' }} />Bu raqamli foydalanuvchi yo'q — faqat «SMS kodlar va limit» tozalanadi (ro'yxatdan o'tish sinovi uchun)</>)
            : 'Raqam kiritilmasa — HAMMA foydalanuvchilarning sinov ma\'lumoti.'}
        </div>
      </div>

      <ErrorBanner>{error}</ErrorBanner>
      {result && <div style={s.success}>{result}</div>}

      {loading && !data ? <Loading /> : data && (
        <div style={s.grid}>
          {data.categories.map((c) => (
            <div key={c.key} style={{ ...s.cat, ...(c.usable ? {} : s.catOff) }}>
              <div style={s.catHead}>
                <span style={s.catTitle}>
                  <span style={s.catIcon}><Icon name={CATEGORY_ICONS[c.key] || 'trash'} size={20} /></span>
                  <span>{stripEmoji(c.label)}</span>
                </span>
                <span style={{ ...s.count, ...(c.usable && c.total > 0 ? {} : s.countZero) }}>{c.usable ? c.total : '—'}</span>
              </div>
              {c.hint && <div style={s.hint}>{c.hint}</div>}
              <div style={s.tables}>
                {c.tables.map((t) => <span key={t.table} style={s.tbl}>{t.table}: {t.count}</span>)}
              </div>
              <button
                className="th-danger-btn"
                style={{ ...s.dangerBtn, ...s.cardBtn }}
                disabled={!!busy || !c.usable || c.total === 0}
                onClick={() => clean(c.key, c.label)}
              >{busy === c.key ? (<><Icon name="clock" size={18} style={{ marginRight: '8px' }} />Tozalanmoqda…</>) : (<><Icon name="trash" size={18} style={{ marginRight: '8px' }} />Tozalash</>)}</button>
            </div>
          ))}
        </div>
      )}

      {data && (
        <div style={{ ...s.card, marginTop: '24px', marginBottom: 0, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '220px', fontSize: '14px', color: 'var(--color-text)' }}>
            Jami: <b>{data.total}</b> ta yozuv{scopeText}
          </div>
          <button
            className="th-danger-btn"
            style={{ ...s.dangerBtn, fontWeight: 700 }}
            disabled={!!busy || data.total === 0}
            onClick={() => clean('all', 'Hammasi')}
          >{busy === 'all' ? (<><Icon name="clock" size={18} style={{ marginRight: '8px' }} />Tozalanmoqda…</>) : (<><Icon name="trash" size={18} style={{ marginRight: '8px' }} />Hammasini tozalash</>)}</button>
        </div>
      )}
    </AdminLayout>
  );
}

const BTN = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
  height: '44px', padding: '0 16px', borderRadius: 'var(--radius-btn)', fontFamily: 'var(--font-family)',
  fontSize: '14px', cursor: 'pointer', whiteSpace: 'nowrap',
};

const s = {
  warn: {
    display: 'flex', alignItems: 'center', gap: '12px',
    background: 'var(--color-primary)', color: 'var(--color-on-primary)',
    borderRadius: 'var(--radius-card)', padding: '16px', fontSize: '14px', lineHeight: 1.45, marginBottom: '24px',
  },
  card: {
    background: 'var(--color-surface-alt)', borderRadius: 'var(--radius-card)', padding: '16px',
    marginBottom: '24px',
  },
  fieldLabel: {
    display: 'flex', alignItems: 'center', marginBottom: '8px',
    fontSize: '14px', fontWeight: '500', color: 'var(--color-text)',
  },
  row: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' },
  rowBtn: { height: '48px' },
  // ikkilamchi: oq fon, 1px #E6E6E6 chegara, qora matn
  secondaryBtn: {
    ...BTN, fontWeight: '500', background: 'var(--color-surface)', color: 'var(--color-text)',
    border: '1px solid var(--color-border)',
  },
  // destruktiv: danger-outline (hover fon — .th-danger-btn)
  dangerBtn: {
    ...BTN, fontWeight: '500', background: 'var(--color-surface)', color: 'var(--color-danger)',
    border: '1px solid var(--color-danger)',
  },
  cardBtn: { width: '100%', marginTop: 'auto' },
  scope: { display: 'flex', alignItems: 'center', marginTop: '12px', fontSize: '12px', color: 'var(--color-text-muted)' },
  success: {
    background: 'rgba(46, 158, 91, 0.12)', borderRadius: 'var(--radius-input)',
    padding: '12px 16px', color: 'var(--color-success)', fontSize: '14px', marginBottom: '16px',
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '8px', alignItems: 'stretch' },
  cat: {
    background: 'var(--color-surface-alt)', borderRadius: 'var(--radius-card)', padding: '16px',
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  catOff: { opacity: 0.5 },
  catHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' },
  catTitle: { display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '14px', color: 'var(--color-text)' },
  // sariq urg'u: qora kontur ikonka sariq yumaloq blok ustida
  catIcon: {
    width: '32px', height: '32px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--color-primary)', color: 'var(--color-on-primary)', borderRadius: 'var(--radius-btn)',
  },
  count: { fontWeight: 700, fontSize: '24px', lineHeight: 1.1, color: 'var(--color-text)' },
  countZero: { color: 'var(--color-text-muted)' },
  hint: { fontSize: '12px', color: 'var(--color-text-muted)' },
  tables: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
  tbl: {
    fontSize: '12px', lineHeight: 1.4, background: 'var(--color-surface)', color: 'var(--color-text)',
    border: '1px solid var(--color-border)', borderRadius: 'var(--radius-chip)', padding: '4px 10px',
  },
};
